import { ChevronDown, Mic, Send } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import noraLogo from '../../assets/nora-logo.png'
import { planCatalogApi } from '../../helpers/PlanCatalogApi'
import { notifyPlanAccessNotice } from '../../helpers/PlanAccessNotice'
import { FORPY_WIDGET_URL, NoraWidgetProps } from './NoraWidget.props'
import * as S from './styles'

export function NoraWidget ({ initialPrompt }: NoraWidgetProps): React.ReactElement {
  const [loadedWidgetUrl, setLoadedWidgetUrl] = useState<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const sessionToken = window.localStorage.getItem('sessionToken')
  const isAuthenticated = !!sessionToken

  useEffect(() => {
    const origin = new URL(FORPY_WIDGET_URL).origin
    const receive = (event: MessageEvent): void => {
      if (event.origin !== origin || event.source !== iframeRef.current?.contentWindow) return
      if (event.data?.type === 'forpy:nora:plan-limit') {
        if (event.data.code === 'SUBSCRIPTION_REQUIRED') window.location.assign('/checkout?motivo=renovacao')
        else notifyPlanAccessNotice({ code: event.data.code, message: event.data.message, resource: 'nora_queries_per_month' })
        return
      }
      if (event.data?.type !== 'forpy:nora:request-session' || typeof event.data.requestId !== 'string' || event.data.requestId.length > 128) return
      const source = iframeRef.current.contentWindow
      const requestId: string = event.data.requestId
      void planCatalogApi.getMyAccess().then(access => {
        source?.postMessage({
          type: 'forpy:nora:session', requestId,
          token: access.canAccessApp ? window.localStorage.getItem('sessionToken') : null,
          code: access.canAccessApp ? undefined : 'SUBSCRIPTION_REQUIRED'
        }, origin)
      }).catch(() => {
        source?.postMessage({ type: 'forpy:nora:session', requestId, token: null, code: 'NORA_SESSION_UNAVAILABLE' }, origin)
      })
    }
    window.addEventListener('message', receive)
    return () => window.removeEventListener('message', receive)
  }, [])

  if (!isAuthenticated) return <></>

  const widgetUrl = initialPrompt
    ? `${FORPY_WIDGET_URL}?prompt=${encodeURIComponent(initialPrompt)}`
    : FORPY_WIDGET_URL

  const isLoaded = loadedWidgetUrl === widgetUrl
  const hasInitialPrompt = initialPrompt != null && initialPrompt.trim() !== ''

  return <S.Container>
    {!isLoaded && (
      <S.NoraLoadingShell role='status' aria-label='Carregando a Nora'>
        <header className='nora-loading-header'>
          <img src={noraLogo} alt='' aria-hidden='true' />
          <strong>Nora</strong>
        </header>

        <main className='nora-loading-conversation' aria-hidden='true'>
          <div className='nora-loading-assistant-message'>
            <span className='nora-loading-assistant-mark' />
            <div className='nora-loading-message-bubble'>
              <span className='nora-loading-line nora-loading-line--long' />
              <span className='nora-loading-line nora-loading-line--medium' />
            </div>
          </div>

          {hasInitialPrompt && (
            <>
              <div className='nora-loading-user-message'>
                <span className='nora-loading-line nora-loading-line--user' />
              </div>
              <div className='nora-loading-thinking'>
                <span className='nora-loading-thinking-mark' />
                <span className='nora-loading-line nora-loading-line--thinking' />
              </div>
            </>
          )}
        </main>

        <footer className='nora-loading-composer' aria-hidden='true'>
          <div className='nora-loading-controls'>
            <button type='button' className='nora-loading-profile-select' disabled tabIndex={-1}>
              <span>Consultora</span>
              <ChevronDown size={16} />
            </button>
            <input
              className='nora-loading-input'
              type='text'
              placeholder='Pergunte à Nora...'
              disabled
              tabIndex={-1}
            />
            <Mic className='nora-loading-mic' size={20} />
            <button type='button' className='nora-loading-send' disabled tabIndex={-1}>
              <Send size={22} />
            </button>
          </div>
          <p>Nora, a IA da Forpy, pode cometer erros. Considere verificar informações importantes.</p>
          <span className='nora-loading-counter'>0/600</span>
        </footer>
      </S.NoraLoadingShell>
    )}

    <S.StyledIframe
      ref={iframeRef}
      key={initialPrompt ?? 'default'} // Force iframe reload when prompt changes
      $isLoaded={isLoaded}
      src={widgetUrl}
      allow="microphone"
      title="Forpy Brazilian Food Regulatory AI Assistant"
      onLoad={() => setLoadedWidgetUrl(widgetUrl)}
    />
  </S.Container>
}
